import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { lockScroll } from "@/lib/scroll";
import { EASE } from "./Reveal";

export default function Lightbox({ items, index, onClose, onIndex }) {
  const open = index !== null;
  const item = open ? items[index] : null;
  const prev = useCallback(() => onIndex((index - 1 + items.length) % items.length), [index, items.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % items.length), [index, items.length, onIndex]);

  useEffect(() => {
    if (!open) return undefined;
    lockScroll(true);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); lockScroll(false); };
  }, [open, onClose, prev, next]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="project-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={item.title}
          className="fixed inset-0 z-[60] flex flex-col bg-[#14171B]/95 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          onClick={onClose}
        >
          <div className="flex items-center justify-between px-5 py-4 sm:px-8" onClick={(e) => e.stopPropagation()}>
            <span className="eyebrow text-[#C9A66B]">{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span>
            <button data-testid="lightbox-close" onClick={onClose} aria-label="Cerrar" className="grid h-11 w-11 place-items-center border border-white/30 text-white hover:bg-white hover:text-[#1E2228]">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center px-4 sm:px-20" onClick={(e) => e.stopPropagation()}>
            <AnimatePresence initial={false}>
              <motion.img
                key={item.id}
                src={item.image}
                alt={item.alt}
                className="absolute max-h-full max-w-[calc(100%-2rem)] object-contain sm:max-w-[calc(100%-10rem)]"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
              />
            </AnimatePresence>
            <button data-testid="lightbox-prev" onClick={prev} aria-label="Imagen anterior" className="absolute left-2 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-black/50 text-white hover:bg-[#B8955F] hover:text-[#1E2228] sm:left-6">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button data-testid="lightbox-next" onClick={next} aria-label="Imagen siguiente" className="absolute right-2 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-black/50 text-white hover:bg-[#B8955F] hover:text-[#1E2228] sm:right-6">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="px-5 py-5 text-center sm:px-8" onClick={(e) => e.stopPropagation()}>
            <p data-testid="lightbox-title" className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-white">{item.title}</p>
            <p className="mt-1 text-sm text-white/75">{item.category} · {item.desc}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
